"use client";

import { useState, type ReactNode } from "react";
import { Check, ChevronDown } from "@/components/icons";
import {
 Popover,
 PopoverContent,
 PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
 ChipClearAction,
 ChipClearSpace,
 ClearableChip,
} from "@/components/ui/filter-controls/filters/chip-clear-action";
import { Button } from "@/components/ui/button";

export interface FilterChipProps {
 label: string;
 value?: string | string[];
 activeCount?: number;
 onClear?: () => void;
 children: ReactNode;
 className?: string;
 align?: "start" | "center" | "end";
 contentClassName?: string;
}

interface FilterChipTriggerProps {
 label: string;
 displayValue: string | null | undefined;
 hasValue: boolean;
 activeCount?: number;
 open: boolean;
 onClear?: () => void;
 className?: string;
}

function ActiveCountBadge({ activeCount }: { activeCount?: number }) {
 if (!activeCount || activeCount <= 0) return null;
 return (
  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-semibold bg-action-fill text-action-fill-foreground rounded-sm">
   {activeCount}
  </span>
 );
}

function FilterChipTrigger({
 label,
 displayValue,
 hasValue,
 activeCount,
 open,
 onClear,
 className,
}: FilterChipTriggerProps) {
 const clearable = hasValue && onClear;
 const trigger = (
  <PopoverTrigger asChild>
   <Button
    type="button"
    variant="outline"
    size="compact"
    className={cn(
     "rounded-sm",
     hasValue ? "bg-muted text-foreground" : "text-muted-foreground",
     className,
    )}
   >
    <span className="truncate max-w-[120px]">
     {displayValue || label}
    </span>

    <ActiveCountBadge activeCount={activeCount} />

    <ChevronDown
     className={cn(
      "w-3.5 h-3.5 transition-transform",
      open && "rotate-180",
     )}
    />

    {clearable ? <ChipClearSpace /> : null}
   </Button>
  </PopoverTrigger>
 );
 if (!clearable) return trigger;
 return (
  <ClearableChip>
   {trigger}
   <ChipClearAction label={label} onClear={onClear} className="right-1" />
  </ClearableChip>
 );
}

function chipHasValue(activeCount: number | undefined, value: string | string[] | undefined) {
 return activeCount
  ? activeCount > 0
  : Array.isArray(value)
   ? value.length > 0
   : Boolean(value);
}

export function FilterChip({
 label,
 value,
 activeCount,
 onClear,
 children,
 className,
 align = "start",
 contentClassName,
}: FilterChipProps) {
 const [open, setOpen] = useState(false);

 const hasValue = chipHasValue(activeCount, value);

 const displayValue = Array.isArray(value)
  ? value.length > 0
   ? value.length === 1
    ? value[0]
    : `${value.length} selected`
   : null
  : value;

 return (
  <Popover open={open} onOpenChange={setOpen}>
   <FilterChipTrigger
    label={label}
    displayValue={displayValue}
    hasValue={hasValue}
    activeCount={activeCount}
    open={open}
    onClear={onClear}
    className={className}
   />

   <PopoverContent
    align={align}
    className={cn(
     "w-auto min-w-[200px] p-0 rounded-sm border-border",
     contentClassName,
    )}
   >
    {children}
   </PopoverContent>
  </Popover>
 );
}

export interface FilterChipSelectProps<TItem extends string> {
 label: string;
 options: { label: string; value: TItem }[];
 value: TItem[];
 onChange: (value: TItem[]) => void;
 multiple?: boolean;
 className?: string;
}

export function FilterChipSelect<TItem extends string>({
 label,
 options,
 value,
 onChange,
 multiple = true,
 className,
}: FilterChipSelectProps<TItem>) {
 const handleToggle = (optionValue: TItem) => {
  if (multiple) {
   if (value.includes(optionValue)) {
    onChange(value.filter((v) => v !== optionValue));
   } else {
    onChange([...value, optionValue]);
   }
  } else {
   onChange(value.includes(optionValue) ? [] : [optionValue]);
  }
 };

 const handleClear = () => onChange([]);

 return (
  <FilterChip
   label={label}
   value={value}
   onClear={value.length > 0 ? handleClear : undefined}
   className={className}
  >
   <div className="p-2">
    <div className="text-xs font-medium text-muted-foreground px-2 py-1">
     {label}
    </div>
    <div className="space-y-0.5 max-h-[240px] overflow-y-auto">
     {options.map((option) => (
      <FilterOption
       key={option.value}
       label={option.label}
       isSelected={value.includes(option.value)}
       onToggle={() => handleToggle(option.value)}
      />
     ))}
    </div>
   </div>
  </FilterChip>
 );
}

export interface FilterChipRangeProps {
 label: string;
 min: number | null;
 max: number | null;
 onChange: (min: number | null, max: number | null) => void;
 prefix?: string;
 className?: string;
}

function RangeInput({
 prefix,
 placeholder,
 value,
 onValueChange,
}: {
 prefix: string;
 placeholder: string;
 value: number | null;
 onValueChange: (value: number | null) => void;
}) {
 return (
  <div className="relative flex-1">
   <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
    {prefix}
   </span>
   <input
    type="number"
    placeholder={placeholder}
    value={value ?? ""}
    onChange={(event) =>
     onValueChange(event.target.value ? Number(event.target.value) : null)
    }
    className="w-full h-8 pl-6 pr-2 text-sm border border-border rounded-sm bg-card text-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/20 focus-visible:ring-[2px]"
   />
  </div>
 );
}

export function FilterChipRange({
 label,
 min,
 max,
 onChange,
 prefix = "$",
 className,
}: FilterChipRangeProps) {
 const hasValue = min !== null || max !== null;
 const displayValue = hasValue
  ? min !== null && max !== null
   ? `${prefix}${min}-${prefix}${max}`
   : min !== null
    ? `${prefix}${min}+`
    : `Up to ${prefix}${max}`
  : null;

 const handleClear = () => onChange(null, null);

 return (
  <FilterChip
   label={label}
   value={displayValue ?? undefined}
   onClear={hasValue ? handleClear : undefined}
   className={className}
  >
   <div className="p-3">
    <div className="text-xs font-medium text-muted-foreground mb-2">
     {label}
    </div>
    <div className="flex items-center gap-2">
     <RangeInput
      prefix={prefix}
      placeholder="Min"
      value={min}
      onValueChange={(nextMin) => onChange(nextMin, max)}
     />
     <span className="text-muted-foreground">–</span>
     <RangeInput
      prefix={prefix}
      placeholder="Max"
      value={max}
      onValueChange={(nextMax) => onChange(min, nextMax)}
     />
    </div>
   </div>
  </FilterChip>
 );
}

export function FilterOption({
 label,
 isSelected,
 onToggle,
}: {
 label: string;
 isSelected: boolean;
 onToggle: () => void;
}) {
 return (
  <Button
   type="button"
   variant="ghost"
   size="compact"
   aria-pressed={isSelected}
   onClick={onToggle}
   className={cn(
    "text-left w-full justify-start rounded-sm text-sm",
    isSelected && "hover:bg-action-tint bg-action-tint text-action",
   )}
  >
   <span
    className={cn(
     "w-4 h-4 rounded-sm border flex items-center justify-center",
     isSelected
      ? "bg-action-fill border-primary text-action-fill-foreground"
      : "border-border",
    )}
   >
    {isSelected && <Check className="size-3" />}
   </span>
   {label}
  </Button>
 );
}
